import React, { useState, useEffect } from "react";
import {
  Eye,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Users,
  User,
  ArrowRight,
  Shield,
  Leaf,
  Award,
  BarChart3,
  Activity,
  Globe,
  Clock,
  Database,
  Server,
  Monitor
} from "lucide-react";

const DashboardSection = () => {
  const [views, setViews] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [quoteEmail, setQuoteEmail] = useState("");
  const [quoteStatus, setQuoteStatus] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  // Définir l'URL de base de l'API dans une constante
  const API_BASE_URL = "https://api.greenecobagsusa.com/api";

  // Fonction pour configurer les en-têtes d'authentification
  const getAuthHeaders = () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };



  const sendQuoteEmail = async () => {
    const trimmedEmail = quoteEmail.trim();

    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setQuoteStatus("Adresse email invalide. Veuillez corriger.");
      return;
    }

    setQuoteLoading(true);
    setQuoteStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/send-quote-email`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ email: trimmedEmail })
      });

      if (!response.ok) {
        throw new Error("Erreur lors de l'envoi de l'email.");
      }

      // const data = await response.json();
      setQuoteStatus("Email sauvegardé avec succès !");

      setQuoteEmail(trimmedEmail); // ✅ garde le nouvel email dans l’input
      setOriginalEmail(trimmedEmail); // ✅ met à jour la référence

    } catch (error) {
      console.error("Erreur d'envoi :", error);
      setQuoteStatus("Échec de l'envoi. Vérifiez l'adresse ou réessayez.");
    } finally {
      setQuoteLoading(false);
    }
  };

  const fetchSavedEmail = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/send-quote-email`, {
        method: "GET",
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error("Impossible de récupérer l'email enregistré.");
      }

      const data = await response.json();
      if (data.email) {
        setQuoteEmail(data.email);       // valeur affichée
        setOriginalEmail(data.email);    // valeur de référence
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'email :", error);
    }
  };



  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  };

  // Fonction pour récupérer les vues
  const fetchViews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/views`, {
        method: 'GET',
        headers: getAuthHeaders(),
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setViews(data.views);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error("Error fetching views:", error);
      const errorMessage = error.message || "Failed to load view count";
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Fonction pour récupérer les utilisateurs
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);

      console.log("Fetching users from:", `${API_BASE_URL}/users`);

      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'GET',
        headers: getAuthHeaders(),
        signal: AbortSignal.timeout(10000), // Timeout de 10 secondes
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Non authentifié. Veuillez vous reconnecter.");
        }
        throw new Error(`Erreur du serveur: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data) {
        setUsers(data.users || []);
        setUsersError(null);
      } else {
        setUsersError("Réponse invalide de l'API");
      }
    } catch (err) {
      console.error("Fetch users error:", err);

      if (err.name === 'TimeoutError') {
        setUsersError("La requête a pris trop de temps. Veuillez réessayer.");
      } else if (err.message.includes("Non authentifié")) {
        setUsersError("Non authentifié. Veuillez vous reconnecter.");
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
        }
      } else if (err.message.includes("fetch")) {
        setUsersError("Impossible de se connecter au serveur. Vérifiez que votre backend est en cours d'exécution.");
      } else {
        setUsersError(`Erreur: ${err.message}`);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    setIsVisible(true);
    fetchViews();
    fetchUsers();
    fetchSavedEmail(); // ✅ récupère l’email existant

    const intervalId = setInterval(fetchViews, 60000);
    return () => clearInterval(intervalId);
  }, []);


  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  const refreshViews = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/views`, {
        method: 'GET',
        headers: getAuthHeaders(),
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setViews(data.views);
      setLoading(false);
    } catch (error) {
      console.error("Error refreshing views:", error);
      const errorMessage = error.message || "Failed to load view count";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const refreshUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    fetchUsers();
  };

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([refreshViews(), refreshUsers()]);
    setRefreshing(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Modern Header */}
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-12`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Monitor className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    Admin Dashboard – Équipe technique : Yassin & Mohssin 💼💻
                  </h1>

                  <p className="text-gray-600 text-lg">Green Eco Bags USA - System Overview</p>
                  <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mt-3"></div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
                  <Activity className="w-4 h-4" />
                  <span>Live Data</span>
                </div>
                <button
                  onClick={refreshAll}
                  className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl disabled:scale-100 disabled:shadow-none overflow-hidden"
                  disabled={loading || usersLoading || refreshing}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                  <div className="flex items-center space-x-3 relative z-10">
                    <RefreshCw className={`w-5 h-5 ${(loading || usersLoading || refreshing) ? 'animate-spin' : ''}`} />
                    <span>Refresh All</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Total Views Card */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 hover:shadow-2xl transition-all duration-300 group">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Total Views</h3>
                    <p className="text-sm text-gray-600">Website visits</p>
                  </div>
                </div>
                <button
                  onClick={refreshViews}
                  className="p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300"
                  disabled={loading}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="flex items-center space-x-3">
                    <div className="animate-pulse bg-gray-200 h-12 w-32 rounded-xl"></div>
                    <div className="animate-pulse bg-gray-200 h-4 w-20 rounded"></div>
                  </div>
                ) : error ? (
                  <div className="flex items-center space-x-3 text-red-500 bg-red-50 p-4 rounded-xl">
                    <AlertCircle className="w-6 h-6" />
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{formatNumber(views)}</div>
                    <div className="flex items-center space-x-2 text-green-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">Live tracking</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Total Users Card */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 hover:shadow-2xl transition-all duration-300 group">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Total Users</h3>
                    <p className="text-sm text-gray-600">Registered accounts</p>
                  </div>
                </div>
                <button
                  onClick={refreshUsers}
                  className="p-3 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all duration-300"
                  disabled={usersLoading}
                >
                  <RefreshCw className={`w-5 h-5 ${usersLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="space-y-4">
                {usersLoading ? (
                  <div className="flex items-center space-x-3">
                    <div className="animate-pulse bg-gray-200 h-12 w-32 rounded-xl"></div>
                    <div className="animate-pulse bg-gray-200 h-4 w-20 rounded"></div>
                  </div>
                ) : usersError ? (
                  <div className="flex items-center space-x-3 text-red-500 bg-red-50 p-4 rounded-xl">
                    <AlertCircle className="w-6 h-6" />
                    <span className="text-sm font-medium">{usersError}</span>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{formatNumber(users.length)}</div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span>{users.filter(user => user.isAdmin).length} admins</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4 text-blue-600" />
                        <span>{users.filter(user => !user.isAdmin).length} users</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* System Status Card */}
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 hover:shadow-2xl transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-6">
                <div className="p-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Activity className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">System Status</h3>
                  <p className="text-sm text-gray-600">Health monitoring</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-lg font-semibold text-green-600">Operational</span>
                </div>
                <div className="text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>Auto-refresh every 60s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* API Status Card */}
          <div className={`transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 hover:shadow-2xl transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-6">
                <div className="p-4 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Server className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">API Status</h3>
                  <p className="text-sm text-gray-600">Backend services</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-lg font-semibold text-green-600">Connected</span>
                </div>
                <div className="text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <span>api.greenecobagsusa.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Endpoints Section */}
        <div className={`transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-12`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
              <h3 className="text-2xl font-bold text-white flex items-center">
                <Database className="mr-3" size={24} />
                API Endpoints
              </h3>
              <p className="text-indigo-100 mt-1">Available backend services</p>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Views API</h4>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <code className="text-sm text-gray-700 break-all">
                      https://api.greenecobagsusa.com/api/views
                    </code>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Users API</h4>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <code className="text-sm text-gray-700 break-all">
                      https://api.greenecobagsusa.com/api/users
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-12`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📩 Savougardez Email qui send notification </h3>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <input
                type="email"
                // placeholder="Saisie fotre adresse email"
                value={quoteEmail}
                onChange={(e) => setQuoteEmail(e.target.value)}
                className="w-full sm:w-2/3 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500"
              />
              <button
                onClick={sendQuoteEmail}
                disabled={
                  quoteLoading ||
                  !quoteEmail.trim() ||
                  quoteEmail === originalEmail ||         // pas modifié
                  !isValidEmail(quoteEmail)               // email invalide
                }
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {quoteLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <ArrowRight className="w-5 h-5" />
                )}
                <span>Saver</span>
              </button>

            </div>
            {quoteStatus && (
              <p className={`mt-2 text-sm text-center ${quoteStatus.includes("succès") ? "text-green-600" : "text-red-600"}`}>
                {quoteStatus}
              </p>
            )}
          </div>
        </div>

        {/* Users Statistics */}
        {!usersLoading && !usersError && users.length > 0 && (
          <div className={`transition-all duration-1000 delay-1200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-12`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  <BarChart3 className="mr-3" size={24} />
                  User Statistics
                </h3>
                <p className="text-green-100 mt-1">Detailed user breakdown</p>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center group">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{users.length}</div>
                    <div className="text-lg font-semibold text-gray-700">Total Users</div>
                    <div className="text-sm text-gray-500 mt-1">All registered accounts</div>
                  </div>

                  <div className="text-center group">
                    <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Shield className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{users.filter(user => user.isAdmin).length}</div>
                    <div className="text-lg font-semibold text-gray-700">Administrators</div>
                    <div className="text-sm text-gray-500 mt-1">Admin privileges</div>
                  </div>

                  <div className="text-center group">
                    <div className="w-20 h-20 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <User className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{users.filter(user => !user.isAdmin).length}</div>
                    <div className="text-lg font-semibold text-gray-700">Standard Users</div>
                    <div className="text-sm text-gray-500 mt-1">Regular accounts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Footer */}
        <div className={`transition-all duration-1000 delay-1400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} text-center`}>
          <div className="bg-white/60 backdrop-blur-lg rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <Leaf className="w-4 h-4 text-green-600" />
                <span>Eco-Friendly</span>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Secure</span>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="w-4 h-4 text-green-600" />
                <span>Reliable</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              © 2024 Green Eco Bags USA. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default DashboardSection;